"use client";

import { useState, useEffect, useRef } from 'react';
import { useGame } from '@/app/providers/GameContextProvider';
import { FaExclamationTriangle } from 'react-icons/fa';

interface MaintenanceInfo {
    enabled: boolean;
    scheduledTime: string | null;
    message: string;
}

export default function MaintenanceWarning({
    showInLobby = true,
    isDismissed = false,
}: {
    showInLobby?: boolean;
    isDismissed?: boolean;
}) {
    const { room } = useGame();
    const [maintenanceInfo, setMaintenanceInfo] = useState<MaintenanceInfo | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<string>('');
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!room) return;

        // Request maintenance info when component mounts (only once)
        if (!hasFetchedRef.current) {
            room.send('requestMaintenanceInfo');
            hasFetchedRef.current = true;
        }

        // Listen for maintenance updates
        room.onMessage('maintenanceUpdate', (data: MaintenanceInfo) => {
            setMaintenanceInfo(data);
        });

        // Cleanup: no need to remove listener, Colyseus handles this on room leave
    }, [room]);

    // Update countdown timer
    useEffect(() => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!maintenanceInfo?.enabled || !maintenanceInfo?.scheduledTime) {
            setTimeRemaining('');
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            const scheduled = new Date(maintenanceInfo.scheduledTime!);
            const diff = scheduled.getTime() - now.getTime();

            if (diff <= 0) {
                setTimeRemaining('imminent');
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            if (hours > 0) {
                setTimeRemaining(`${hours}h ${minutes}m`);
            } else if (minutes > 0) {
                setTimeRemaining(`${minutes}m ${seconds}s`);
            } else {
                setTimeRemaining(`${seconds}s`);
            }
        };

        // Update immediately
        updateCountdown();

        // Update every second
        intervalRef.current = setInterval(updateCountdown, 1000);

        // Cleanup on unmount or when dependencies change
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [maintenanceInfo]);

    // Don't show if not enabled, not in lobby, or dismissed
    if (!maintenanceInfo?.enabled || !showInLobby || isDismissed) {
        return null;
    }

    return (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[10000] w-full max-w-2xl px-4">
            <div className="bg-red-900/90 border-2 border-red-500 rounded-lg p-4 shadow-2xl backdrop-blur-sm animate-pulse-slow">
                <div className="flex items-start gap-3">
                    <FaExclamationTriangle className="text-red-400 text-2xl mt-1 flex-shrink-0 animate-pulse" />
                    <div className="flex-1">
                        <h3 className="text-red-300 font-bold text-lg mb-1">
                            Scheduled Maintenance
                        </h3>
                        {maintenanceInfo.scheduledTime && (
                            <p className="text-red-200 text-base font-semibold mb-2">
                                {timeRemaining === 'imminent' ? (
                                    'Maintenance starting now!'
                                ) : timeRemaining ? (
                                    `Maintenance in ${timeRemaining}`
                                ) : (
                                    `Scheduled: ${new Date(maintenanceInfo.scheduledTime).toLocaleString()}`
                                )}
                            </p>
                        )}
                        {maintenanceInfo.message ? (
                            <p className="text-red-100 text-sm">
                                {maintenanceInfo.message}
                            </p>
                        ) : (
                            <p className="text-red-200 text-sm">
                                Server maintenance will begin soon. Please finish your current game.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
